import {
  getFirestoreDocData,
  writeFirestoreDoc,
} from "../../../apis/firestoreAction";
import { firestore, getFirebaseUserInfo } from "../../../firebase";
import { zonedTimeToUtc } from "date-fns-tz";
import { userLogging } from "../../../apis/userLog";

// 経営診断確認個別項目の型
export interface KEIEI_SHINDAN_DATA {
  tenpo_code: string;
  id: string;
  last_time_id: string;
  last_date: string;
  zone_code: string;
  do_code: string;
  approve_day: string;
  last_approve_day: string;
  update_user: string;
  rows: Array<KEIEI_SHINDAN_BIG_RECORD>;
  other_data: KEIEI_SHINDAN_OTHER_DATA;
  score_data: KEIEI_SHINDAN_SCORE_DATA;
  complete_date?: string;
  pdf_path?: string;
}

// 経営診断確認の大項目単位の値の型
export interface KEIEI_SHINDAN_BIG_RECORD {
  big_title: string;
  big_item_id: number;
  big_item_score_no: string;
  big_item_score_name: string;
  rows: Array<KEIEI_SHINDAN_RECORD>;
}

// 経営診断確認の小項目単位の値の型
export interface KEIEI_SHINDAN_RECORD {
  key: string; // 「2-3」のような「{大項目}-{小項目}」の形式
  big_item_id: number;
  small_title1: string;
  small_title2: string;
  small_item_id: number;
  score_list?: Array<number>;
  score: string;
  score_pattern?: string;
  small_item_type?: string;
  small_item_no?: string;
  button_value: number;
  last_score: string;
  small_info_message: string[];
  small_item_data_type?: string;
  photo_filepath_gcs?: string[];
}
// 経営診断確認の項目の点数の値の型
export interface SCORE_MAP {
  id: number;
  name: string;
  value: string;
}
// 経営診断確認の合計得点の値の型
export interface KEIEI_SHINDAN_SCORE_DATA {
  total_score?: string;
  scores?: Array<KEIEI_SHINDAN_TOTAL_SCORE>;
  score5?: string;
  junshu_score?: string;
}
// 経営診断確認の項目ごとの合計点数の値の型
export interface KEIEI_SHINDAN_TOTAL_SCORE {
  no: string;
  name: string;
  score: string;
  max_score: string;
  minus_per_score?: string;
}
// 経営診断確認の大項目単位の値の型
export interface KEIEI_SHINDAN_OTHER_DATA {
  dm_comment?: string;
  dm_comment_user?: string;
  employee?: any;
  early_morning_contact?: any;
  morning_contact?: any;
  afternoon_contact?: any;
  evening_contact?: any;
  late_night_contact?: any;
  total_count?: string;
  last_employee?: string;
  last_early_morning_contact?: string;
  last_morning_contact?: string;
  last_afternoon_contact?: string;
  last_evening_contact?: string;
  last_late_night_contact?: string;
  last_total_count?: string;
  acquired_tenpo_check_record_id?: string;
  acquired_one_vision_record_id?: string;
  acquired_ts_check_record_id?: string;
}
// Firestoreより2レコード取得して結果をマージする
export const getKeieiShindanData = async (
  tenpoCode: string,
  docId: string,
  lastDocId: string
) => {
  // 1:2レコード取得する
  // 大項目番号1～7の共通大項目の場合
  const baseItemValues = await getFirestoreDocData(
    "keiei_shindan_items",
    "000000_basedata"
  );

  let retDocId = null;

  if (docId) {
    retDocId = await getFirestoreDocData("keiei_shindan_records", docId);
    if (!retDocId) {
      // firestoreからデータが取得できないとき（初回でデータがない場合）必要な情報を以下作成
    }
  }
  const docIdData: any = retDocId;
  let lastMonth: any = null;
  let lastOtherData: any = null;
  if (docIdData) {
    let lastResult = await getJsonlastMonths(docIdData.docId);
    lastMonth = lastResult["records"];
    lastOtherData = lastResult["otherData"];
  }
  const lastMonthRecords = lastMonth;
  return editData(
    baseItemValues,
    docIdData,
    tenpoCode,
    docId,
    lastDocId,
    lastMonthRecords,
    lastOtherData
  );
};
const editData = (
  baseItemValues: any,
  docIdData: any,
  tenpoCode: string,
  docId: string,
  lastDocId: string,
  lastMonthRecords: any,
  lastOtherData: any
) => {
  const data: KEIEI_SHINDAN_DATA = {
    tenpo_code: tenpoCode,
    id: docId,
    last_time_id: lastDocId,
    last_date: docIdData ? docIdData.attribution.last_date : "",
    zone_code: "",
    do_code: "",
    approve_day: docIdData ? docIdData.attribution.approve_day : "",
    complete_date: docIdData ? docIdData.attribution.complete_date : "",
    pdf_path: docIdData ? docIdData.attribution.pdf_path : "",
    last_approve_day: docIdData ? docIdData.attribution.last_approve_day : "",
    update_user: docIdData ? docIdData.update_date.update_user : "",
    other_data: {},
    score_data: {},
    rows: [],
  };
  let smallValueArray: Array<KEIEI_SHINDAN_RECORD> = [];
  const keys = docIdData ? sortObject(docIdData["records"]) : [];
  Object.keys(keys)
    .sort()
    .forEach(function (key: any) {
      let recordKey = "records-" + keys[key]["key"];
      let lastMonthScore = "";
      if (lastMonthRecords && lastMonthRecords[recordKey]) {
        lastMonthScore = lastMonthRecords[recordKey]["score"];
      }

      let scorePatternList = [];
      scorePatternList = keys[key]["score_list"];

      const smallValue: KEIEI_SHINDAN_RECORD = {
        key: keys[key]["key"],
        big_item_id: keys[key]["big_item_id"],
        small_title1: keys[key]["small_title1"],
        small_title2: keys[key]["small_title2"],
        small_item_id: keys[key]["small_item_id"],
        score: keys[key]["score"],
        score_list: scorePatternList,
        score_pattern: keys[key]["score_pattern"],
        small_item_type: keys[key]["small_item_type"] || "",
        small_item_no: keys[key]["small_item_no"] || "",
        button_value: keys[key]["button_value"] || "",
        last_score: lastMonthScore,
        small_info_message: keys[key]["small_info_message"] || [],
        small_item_data_type: keys[key]["small_item_data_type"] || "",
        photo_filepath_gcs: keys[key]["photo_filepath_gcs"] || [],
      };
      smallValueArray.push(smallValue);
    });

  let othersValue: KEIEI_SHINDAN_OTHER_DATA = {};
  const othersKeys = docIdData ? docIdData["other_data"] : {};
  if (othersKeys) {
    othersValue = {
      dm_comment: othersKeys["dm_comment"] || "",
      dm_comment_user: othersKeys["dm_comment_user"] || "",
      employee: othersKeys["employee"]
        ? othersKeys["employee"]
        : String(othersKeys["employee"]) === "0"
        ? "0"
        : "",
      early_morning_contact: othersKeys["early_morning_contact"]
        ? othersKeys["early_morning_contact"]
        : String(othersKeys["early_morning_contact"]) === "0"
        ? "0"
        : "",
      morning_contact: othersKeys["morning_contact"]
        ? othersKeys["morning_contact"]
        : String(othersKeys["morning_contact"]) === "0"
        ? "0"
        : "",
      afternoon_contact: othersKeys["afternoon_contact"]
        ? othersKeys["afternoon_contact"]
        : String(othersKeys["afternoon_contact"]) === "0"
        ? "0"
        : "",
      evening_contact: othersKeys["evening_contact"]
        ? othersKeys["evening_contact"]
        : String(othersKeys["evening_contact"]) === "0"
        ? "0"
        : "",
      late_night_contact: othersKeys["late_night_contact"]
        ? othersKeys["late_night_contact"]
        : String(othersKeys["late_night_contact"]) === "0"
        ? "0"
        : "",
      total_count: othersKeys["total_count"]
        ? othersKeys["total_count"]
        : String(othersKeys["total_count"]) === "0"
        ? "0"
        : "",
      last_employee: lastOtherData ? lastOtherData["employee"] : "",
      last_early_morning_contact: lastOtherData
        ? lastOtherData["early_morning_contact"]
        : "",
      last_morning_contact: lastOtherData
        ? lastOtherData["morning_contact"]
        : "",
      last_afternoon_contact: lastOtherData
        ? lastOtherData["afternoon_contact"]
        : "",
      last_evening_contact: lastOtherData
        ? lastOtherData["evening_contact"]
        : "",
      last_late_night_contact: lastOtherData
        ? lastOtherData["late_night_contact"]
        : "",
      last_total_count: lastOtherData ? lastOtherData["total_count"] : "",
      acquired_tenpo_check_record_id:
        othersKeys["acquired_tenpo_check_record_id"] || "",
      acquired_one_vision_record_id:
        othersKeys["acquired_one_vision_record_id"] || "",
      acquired_ts_check_record_id:
        othersKeys["acquired_ts_check_record_id"] || "",
    };
  }
  data["other_data"] = othersValue;

  let bigValueArray: Array<KEIEI_SHINDAN_BIG_RECORD> = [];
  let scoresValueArray: Array<KEIEI_SHINDAN_TOTAL_SCORE> = [];
  const scoreMap = docIdData ? docIdData["score_data"] : {};
  const scoreKeys = scoreMap ? scoreMap["scores"] : [];
  if (scoreKeys) {
    Object.keys(scoreKeys)
      .sort()
      .forEach(function (key: any) {
        const smallValue: KEIEI_SHINDAN_TOTAL_SCORE = {
          no: scoreKeys[key]["no"],
          name: scoreKeys[key]["name"],
          score: scoreKeys[key]["score"],
          max_score: scoreKeys[key]["max_score"],
          minus_per_score: scoreKeys[key]["minus_per_score"],
        };
        scoresValueArray.push(smallValue);
      });
  }
  data["score_data"] = {
    total_score: scoreMap ? scoreMap["total_score"] : "0",
    scores: scoresValueArray,
    score5: scoreMap ? scoreMap["score5"] : "0",
    junshu_score: scoreMap ? scoreMap["junshu_score"] : "0",
  };

  // 大項目(共通大項目が10を超えるとソートが崩れるので注意!)
  Object.keys(baseItemValues.attribution)
    .sort()
    .forEach(function (key) {
      const bigValue: KEIEI_SHINDAN_BIG_RECORD = {
        big_title: baseItemValues.attribution[key].big_item_name,
        big_item_id: baseItemValues.attribution[key].big_item_id,
        big_item_score_no: baseItemValues.attribution[key].big_item_score_no,
        big_item_score_name:
          baseItemValues.attribution[key].big_item_score_name,
        // 前で作成した小項目の配列より同じ大項目の項目の値を設定する
        rows: makeBigRecordArray(
          baseItemValues.attribution[key].big_item_id,
          baseItemValues.attribution[key].small_items,
          smallValueArray,
          baseItemValues.attribution[key].score_pattern
        ),
      };
      bigValueArray.push(bigValue);
    });
  data["rows"] = bigValueArray;
  return data;
};

const makeBigRecordArray = (
  bigItemId: string,
  smallItems: any,
  smallValueArray: Array<KEIEI_SHINDAN_RECORD>,
  score_pattern?: Array<SCORE_MAP>
) => {
  const searchRecord = (
    bigItemId: string,
    smallItemId: string,
    smallItemType: string,
    smallItemNo: string,
    smallItemTitle1: string,
    smallItemTitle2: string,
    smallScorePattern: any,
    smallScoreList: Array<any>,
    smallButtonValue: string,
    smallInfoMessage: Array<any>,
    smallItemDataType: string,
    photoFilepathGcs: Array<any>
  ) => {
    const key = bigItemId + "-" + smallItemId;
    smallItemTitle2 = smallItemTitle2 || "";
    let scorePatternList: any[] = [];
    if (smallScoreList) {
      scorePatternList = smallScoreList;
    }
    for (const row of smallValueArray) {
      if (row["key"] === key) {
        row["small_title1"] = smallItemTitle1;
        row["small_title2"] = smallItemTitle2;
        row["score_list"] = scorePatternList;
        row["small_item_no"] = smallItemNo || "";
        row["small_item_type"] = smallItemType || "";
        row["small_info_message"] = smallInfoMessage || [];
        row["small_item_data_type"] = smallItemDataType || "";
        row["photo_filepath_gcs"] = photoFilepathGcs || [];
        return row;
      }
    }
    const tmp: KEIEI_SHINDAN_RECORD = {
      key,
      big_item_id: Number(bigItemId),
      small_title1: smallItemTitle1,
      small_title2: smallItemTitle2,
      small_item_id: Number(smallItemId),
      score: "",
      score_pattern: smallScorePattern || score_pattern || "",
      score_list: scorePatternList,
      small_item_type: smallItemType || "",
      small_item_no: smallItemNo || "",
      button_value: Number(smallButtonValue),
      last_score: "",
      small_info_message: smallInfoMessage || [],
      small_item_data_type: smallItemDataType || "",
      photo_filepath_gcs: photoFilepathGcs || [],
    };
    return tmp;
  };
  let array: Array<KEIEI_SHINDAN_RECORD> = [];
  for (const row of smallItems) {
    const val = searchRecord(
      bigItemId,
      row.id,
      row.item_type,
      row.item_no,
      row.name,
      row.name2,
      row.score_pattern,
      row.score_list,
      row.button_value,
      row.info_message,
      row.item_data_type,
      row.photo_filepath_gcs
    );
    if (val !== undefined) {
      array.push(val);
    }
  }
  return array;
};

// 配列の順番を整えるため、ここだけ連想配列キーを「xx-yy(例:02-11)」のような形にする
const sortObject = (objects: any) => {
  let newObj: any = {};
  Object.keys(objects).forEach(function (key) {
    const tempKey =
      ("00" + objects[key]["big_item_id"]).slice(-2) +
      ("00" + objects[key]["small_item_id"]).slice(-2);
    newObj[tempKey] = objects[key];
  });
  return newObj;
};

/**
 * KeieiShindanの変更項目を差分更新し、最終的にReactHooksに保存する
 * @param keieiShindanData KeieiShindanのオブジェクト
 * @returns
 */
export const updateKeieiShindan = (
  keieiShindanData: any,
  newData: KEIEI_SHINDAN_RECORD
) => {
  const objects: any = {};
  const array: any = [];
  for (const [key, obj] of Object.entries(keieiShindanData)) {
    if (key === newData.key) {
      objects[key] = newData;
    } else {
      objects[key] = obj;
    }
    array.push(objects[key]);
  }
  return array;
};

/**
 * KeieiShindanの値を受け取り、Firestoreの更新を行う
 */
export const updateKeieiShindanFirestore = (
  keieiShindanData: Array<KEIEI_SHINDAN_BIG_RECORD>,
  keieiShindanId: string,
  keieiShindanLastTimeId: string,
  selectedTenpoCode: string,
  keieiShindanOtherData: KEIEI_SHINDAN_OTHER_DATA,
  approveDay: string,
  lastApproveDay: string,
  userPrivilege: string
) => {
  if (
    (Array.isArray(keieiShindanData) && keieiShindanData.length === 0) ||
    keieiShindanId === ""
  ) {
    // 初回読み込みで呼ばれてしまうので空であれば終了する
    return;
  }
  const objects: any = {};
  objects["attribution"] = {
    conditions: ["ALL"],
    last_time_id: keieiShindanLastTimeId,
    tenpo_code: selectedTenpoCode,
    approve_day: approveDay,
    last_approve_day: lastApproveDay,
    user_privilege: userPrivilege,
  };
  objects["id"] = keieiShindanId;
  objects["docId"] = keieiShindanId;
  objects["update_date"] = {
    update_date: firestore.FieldValue.serverTimestamp(),
    update_user: getFirebaseUserInfo().uid,
  };
  objects["other_data"] = keieiShindanOtherData;
  const JUNSHU_ID = 6;
  const ROUMU_ID = 5;
  let records: any = {};
  let scores: Array<KEIEI_SHINDAN_TOTAL_SCORE> = [];
  let allTotalScore: number = 0;
  let junshuScore = 0;
  let score5 = 0;
  let minusScore: number = 0;
  let minusMaxScore: number = 0;
  let junshuName = "遵守事項";
  for (const bigRecord of keieiShindanData) {
    let bigItemId: number = bigRecord.big_item_id;
    // eslint-disable-next-line eqeqeq
    if (bigItemId == JUNSHU_ID) {
      // junshuName = bigRecord.big_item_score_name;
      for (const smallRecord of bigRecord.rows) {
        const keyValue: string = "records-" + smallRecord.key;
        records[keyValue] = smallRecord;
        if (smallRecord["score"]) {
          junshuScore += Number(smallRecord["score"]);
          minusMaxScore += 5;
          if (Number(smallRecord["score"]) === 0) {
            minusScore += 5;
          }
        }
      }
    }
  }

  for (const bigRecord of keieiShindanData) {
    let bigItemId: number = bigRecord.big_item_id || 0;
    // eslint-disable-next-line eqeqeq
    if (bigItemId == JUNSHU_ID) {
      continue;
    }
    let totalScore: number = 0;
    let maxScore: number = 0;
    for (const smallRecord of bigRecord.rows) {
      const keyValue: string = "records-" + smallRecord.key;
      records[keyValue] = smallRecord;
      if (smallRecord["score"]) {
        totalScore += Number(smallRecord["score"]);
        if (bigItemId == ROUMU_ID) {
          minusMaxScore += 2;
          if (Number(smallRecord["score"]) === 0) {
            minusScore += 2;
          }
        }
        if (smallRecord["score_list"]) {
          maxScore += Number(smallRecord["score_list"][0]);
        }
      }
    }
    score5 = totalScore;

    let scoreName = bigRecord.big_item_score_name;
    if (bigItemId == ROUMU_ID) {
      totalScore += junshuScore;
      scoreName += " " + junshuName;
    }
    let minusScorePercent = Math.round((minusScore / minusMaxScore) * 100);
    allTotalScore += totalScore;
    scores.push({
      no: bigRecord.big_item_score_no,
      name: scoreName,
      score: String(totalScore),
      max_score: String(maxScore),
      minus_per_score: String(minusScorePercent),
    });
  }

  objects["records"] = records;

  objects["score_data"] = {
    scores: scores,
    total_score: String(allTotalScore),
    score5: score5,
    junshu_score: junshuScore,
  };
  userLogging(
    "KeieiShindan",
    "新規作成#keiei_shindan_recordsテーブル更新",
    JSON.stringify(objects)
  );

  writeFirestoreDoc("keiei_shindan_records", keieiShindanId, objects);
};
export const getTenpoFirestoreData = async (tenpoCode: string) => {
  const data = await getFirestoreDocData("existing_sej_stores", tenpoCode).then(
    (response: any) => {
      return response;
    }
  );
  return data;
};

export function getFirstDayOfMonth(target_date: string, addMonth: number) {
  // 日付取得
  let today = zonedTimeToUtc(target_date, "Asia/Tokyo");
  let this_year = today.getFullYear();
  let this_month = today.getMonth();

  // 年月
  let firstDayOfMonth = new Date(this_year, this_month + addMonth, 1);
  let year = firstDayOfMonth.getFullYear() + "";
  let month =
    firstDayOfMonth.getMonth() + 1 < 10
      ? "0" + (firstDayOfMonth.getMonth() + 1)
      : firstDayOfMonth.getMonth() + 1 + "";
  //文字列を作成
  return year + month + "01";
}

export function getLastDayOfMonth(target_date: string, addMonth: number) {
  // 日付取得
  let today = zonedTimeToUtc(target_date, "Asia/Tokyo");
  let this_year = today.getFullYear();
  let this_month = today.getMonth();

  // 年月
  let lastDayOfMonth = new Date(this_year, this_month + addMonth, 0);
  let year = lastDayOfMonth.getFullYear() + "";
  let month =
    lastDayOfMonth.getMonth() + 1 < 10
      ? "0" + (lastDayOfMonth.getMonth() + 1)
      : lastDayOfMonth.getMonth() + 1 + "";
  let date = lastDayOfMonth.getDate() + "";
  //文字列を作成
  return year + month + date;
}

async function getJsonlastMonths(last_time_id: string) {
  let retDocItem: any = null;
  let retRecords: any = {};
  let otherData: any = {};
  let resultData: any = {};
  let base_month: string = "";
  // 過去1カ月分取得する
  let n: number = 1;
  let count: number = 0;
  for (let i: number = 0; i < n && count < 10; count++) {
    try {
      if (last_time_id) {
        retDocItem = await getFirestoreDocData(
          "keiei_shindan_records",
          last_time_id
        );
        if (retDocItem) {
          if (base_month === "") {
            base_month = getFirstDayOfMonth(
              retDocItem.attribution.approve_day,
              0
            );
          }
          let target_month = getFirstDayOfMonth(
            retDocItem.attribution.approve_day,
            0
          );
          let last_month = getFirstDayOfMonth(base_month, -1 * i);
          let complete_date = retDocItem.attribution.complete_date;
          last_time_id = retDocItem.attribution.last_time_id;
          if (complete_date && last_month !== target_month) {
            retRecords = retDocItem.records;
            otherData = retDocItem.other_data;
            i++;
          }
        }
      } else {
        break;
      }
    } catch {
      break;
    }
  }
  resultData["records"] = retRecords;
  resultData["otherData"] = otherData;
  return resultData;
}

// keiei_shindan_recordsのデータを取得
export const getKeieiShindanRecordsFirestoreData = async (
  keieiShindanId: string
) => {
  const data = await getFirestoreDocData(
    "keiei_shindan_records",
    keieiShindanId
  ).then((response: any) => {
    return response;
  });
  return data;
};

// 保存済みのレコードデータを取得する
export const getKeieiShindanMaxScore = async (
  bigItemId: string,
  smallItemId: string
) => {
  // レコードを取得する
  // 大項目番号1～7の共通大項目の場合
  const baseItemValues: any = await getFirestoreDocData(
    "keiei_shindan_items",
    "000000_basedata"
  );

  let maxScore = 0;
  if (baseItemValues) {
    Object.keys(baseItemValues.attribution)
      .sort()
      .forEach(function (key) {
        if (baseItemValues.attribution[key].big_item_id === bigItemId) {
          let smallItems = baseItemValues.attribution[key].small_items;
          for (const smallItem of smallItems) {
            if (String(smallItem.id) === smallItemId && smallItem.score_list) {
              maxScore = Number(smallItem.score_list[0]);
              break;
            }
          }
        }
      });
  }

  return maxScore;
};

// 保存済みのレコードデータを取得する
export const getKeieiShindanRegistedData = async (
  tenpoCode: string,
  docId: string
) => {
  // レコードを取得する
  // 大項目番号1～7の共通大項目の場合
  const baseItemValues = await getFirestoreDocData(
    "keiei_shindan_items",
    "000000_basedata"
  );

  let retDocId = null;
  if (docId) {
    retDocId = await getFirestoreDocData("keiei_shindan_records", docId);
  }
  const docIdData: any = retDocId;

  return editReadData(baseItemValues, docIdData, tenpoCode, docId);
};

const editReadData = (
  baseItemValues: any,
  docIdData: any,
  tenpoCode: string,
  docId: string
): KEIEI_SHINDAN_DATA => {
  const data: KEIEI_SHINDAN_DATA = {
    tenpo_code: tenpoCode,
    id: docId,
    last_time_id: docIdData ? docIdData.last_time_id : "",
    zone_code: "",
    do_code: "",
    approve_day: docIdData ? docIdData.attribution.approve_day : "",
    complete_date: docIdData ? docIdData.attribution.complete_date : "",
    pdf_path: docIdData ? docIdData.attribution.pdf_path : "",
    last_approve_day: docIdData ? docIdData.attribution.last_approve_day : "",
    update_user: docIdData ? docIdData.update_date.update_user : "",
    last_date: docIdData ? docIdData.last_date : "",
    rows: [],
    other_data: docIdData ? docIdData.other_data : "",
    score_data: docIdData ? docIdData.score_data : "",
  };

  let smallValueArray: Array<KEIEI_SHINDAN_RECORD> = [];
  const keys = docIdData ? sortObject(docIdData["records"]) : [];
  Object.keys(keys)
    .sort()
    .forEach(function (key: any) {
      const smallValue: KEIEI_SHINDAN_RECORD = {
        key: keys[key]["key"],
        big_item_id: keys[key]["big_item_id"],
        // small_title: keys[key]["small_title"],
        small_item_id: keys[key]["small_item_id"],
        score: keys[key]["score"],
        button_value: keys[key]["button_value"],
        last_score: keys[key]["last_score"],
        small_title1: keys[key]["small_title1"],
        small_title2: keys[key]["small_title2"],
        score_pattern: keys[key]["score_pattern"] || "",
        small_item_type: keys[key]["small_item_type"] || "",
        small_item_no: keys[key]["small_item_no"] || "",
        small_info_message: keys[key]["small_info_message"] || [],
        small_item_data_type: keys[key]["small_item_data_type"] || "",
        photo_filepath_gcs: keys[key]["photo_filepath_gcs"] || [],
      };
      smallValueArray.push(smallValue);
    });

  let bigValueArray: Array<KEIEI_SHINDAN_BIG_RECORD> = [];

  // 大項目(共通大項目が10を超えるとソートが崩れるので注意!)
  Object.keys(baseItemValues.attribution)
    .sort()
    .forEach(function (key) {
      const bigValue: KEIEI_SHINDAN_BIG_RECORD = {
        big_title: baseItemValues.attribution[key].big_item_name,
        big_item_id: baseItemValues.attribution[key].big_item_id,
        big_item_score_no: baseItemValues.attribution[key].big_item_score_no,
        big_item_score_name:
          baseItemValues.attribution[key].big_item_score_name,
        // 前で作成した小項目の配列より同じ大項目の項目の値を設定する
        rows: makeBigRecordArray(
          baseItemValues.attribution[key].big_item_id,
          baseItemValues.attribution[key].small_items,
          smallValueArray,
          baseItemValues.attribution[key].score_pattern
        ),
      };
      bigValueArray.push(bigValue);
    });

  data["rows"] = bigValueArray;

  return data;
};
